import { COUNTRIES } from "@/constants/countries";
import { useThemeColor } from "@/hooks/useThemeColor";
import { VehiclePlate } from "@/types/vehicle-plate";
import { isValidPlateForCountry } from "@/utils/validateVehiclePlate";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Alert, StyleSheet, TextInput, TouchableOpacity } from "react-native";
import { ThemedText } from "../ThemedText";
import { ThemedView } from "../ThemedView";
import { VehiclePlateScrollView } from "./VehiclePlateScrollView";

type VehiclePlateInputProps = {
  plates: VehiclePlate[];
  onChange: (
    plates: VehiclePlate[] | ((prevPlates: VehiclePlate[]) => VehiclePlate[]),
  ) => void;
  limit: number;
  countries?: { label: string; value: string; flag: string }[];
  defaultCountry?: string;
  renderList?: boolean;
  onPendingChange?: (hasPending: boolean) => void;
  onCountryChange?: (country: string) => void;
};

const VehiclePlateInput = forwardRef(
  (
    {
      plates,
      onChange,
      limit = 3,
      countries = COUNTRIES,
      defaultCountry,
      renderList = true,
      onPendingChange,
      onCountryChange,
    }: VehiclePlateInputProps,
    ref,
  ) => {
    const [inputValue, setInputValue] = useState("");
    const [selectedCountry, setSelectedCountry] = useState(
      defaultCountry || countries[0].value,
    );
    const [modalVisible, setModalVisible] = useState(false);
    const [showInvalidError, setShowInvalidError] = useState(false);
    const inputRef = useRef<TextInput>(null);
    const borderColor = useThemeColor({}, "icon");
    const color = useThemeColor({}, "text");
    const backgroundColor = useThemeColor({}, "background");
    const isFull = plates.length >= limit;
    const { t } = useTranslation();

    useEffect(() => {
      if (defaultCountry) {
        setSelectedCountry(defaultCountry);
      }
    }, [defaultCountry]);

    useEffect(() => {
      const trimmed = inputValue.trim();
      if (!trimmed || isValidPlateForCountry(selectedCountry, trimmed)) {
        setShowInvalidError(false);
      }
    }, [inputValue, selectedCountry]);

    useEffect(() => {
      onPendingChange?.(inputValue.trim().length > 0);
    }, [inputValue, onPendingChange]);

    const addPlate = (plateToAdd: string): boolean => {
      if (
        plates.some(
          (p) => p.plate === plateToAdd && p.country === selectedCountry,
        )
      ) {
        Alert.alert(t("DUPLICATE_PLATE"), t("VEHICLE_PLATE_ALREADY_ADDED"));
        return false;
      }
      if (!isValidPlateForCountry(selectedCountry, plateToAdd)) {
        setShowInvalidError(true);
        return false;
      }
      if (plates.length < limit) {
        onChange([...plates, { plate: plateToAdd, country: selectedCountry }]);
        return true;
      }
      return false;
    };

    const handleInputChange = (text: string) => {
      if (isFull) return;

      const sanitizedText = text
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, 10);

      setInputValue(sanitizedText);
    };

    const handleSubmitEditing = () => {
      if (isFull || !inputValue.trim()) return;

      if (addPlate(inputValue.trim())) {
        setInputValue("");
      } else {
        inputRef.current?.focus();
      }
    };

    useImperativeHandle(ref, () => ({
      submit: (): boolean => {
        const trimmed = inputValue.trim();
        if (!trimmed) return true;
        if (addPlate(trimmed)) {
          setInputValue("");
          return true;
        }
        inputRef.current?.focus();
        return false;
      },
      getPendingPlateCandidate: () => {
        const sanitizedInput = inputValue
          .toUpperCase()
          .replace(/[^A-Z0-9]/g, "")
          .trim();

        if (!sanitizedInput) {
          return null;
        }

        if (!isValidPlateForCountry(selectedCountry, sanitizedInput)) {
          setShowInvalidError(true);
          return null;
        }

        return {
          plate: sanitizedInput,
          country: selectedCountry,
        };
      },
      clearInput: () => {
        setInputValue("");
        setShowInvalidError(false);
      },
    }));

    const selectedCountryData = COUNTRIES.find(
      (c) => c.value === selectedCountry,
    );

    const CountryPickerModal = () =>
      createPortal(
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10000,
          }}
          onClick={() => setModalVisible(false)}
        >
          <div
            style={{
              backgroundColor,
              borderRadius: 20,
              padding: 10,
              width: "90%",
              maxWidth: 400,
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ maxHeight: 300, overflowY: "auto" }}>
              {countries.map((item) => (
                <TouchableOpacity
                  key={item.value}
                  style={styles.countryItem}
                  onPress={() => {
                    setSelectedCountry(item.value);
                    setModalVisible(false);
                    onCountryChange?.(item.value);
                  }}
                >
                  <ThemedText style={styles.countryFlag}>
                    {item.flag}
                  </ThemedText>
                  <ThemedText style={styles.countryName}>
                    {item.label}
                  </ThemedText>
                </TouchableOpacity>
              ))}
            </div>
          </div>
        </div>,
        document.body,
      );

    return (
      <ThemedView style={styles.container}>
        <ThemedView style={styles.inputWrapper}>
          <ThemedView style={[styles.inputContainer, { borderColor }]}>
            <TouchableOpacity
              style={styles.flagButton}
              onPress={() => setModalVisible(true)}
            >
              <ThemedText style={styles.flagText}>
                {selectedCountryData?.flag}
              </ThemedText>
            </TouchableOpacity>
            <TextInput
              ref={inputRef}
              style={
                [
                  styles.input,
                  { color },
                  isFull && styles.disabledInput,
                  { outlineStyle: "none" },
                ] as any
              }
              placeholder={selectedCountryData?.vehiclePlatePlaceholder || t("VEHICLE_PLATES_PLACEHOLDER")}
              placeholderTextColor={isFull ? "#a0a0a0" : borderColor}
              value={inputValue}
              onChangeText={handleInputChange}
              onSubmitEditing={handleSubmitEditing}
              returnKeyType="done"
              autoCapitalize="characters"
              editable={!isFull}
            />
          </ThemedView>
          <ThemedView style={styles.errorSlot}>
            {isFull ? (
              <ThemedText style={styles.errorText}>
                {t("MAXIMUM_NUMBER_OF_PLATES_REACHED")}
              </ThemedText>
            ) : showInvalidError ? (
              <ThemedText style={styles.errorText}>
                {t("INVALID_PLATE_FORMAT_HINT", {
                  country: selectedCountryData?.label ?? selectedCountry,
                })}
              </ThemedText>
            ) : null}
          </ThemedView>
        </ThemedView>
        {renderList && (
          <VehiclePlateScrollView plates={plates} onChange={onChange} />
        )}
        {modalVisible && <CountryPickerModal />}
      </ThemedView>
    );
  },
);

const styles = StyleSheet.create({
  container: {
    flexDirection: "column",
    gap: 8,
    marginBottom: 16,
  },
  inputWrapper: {
    width: "100%",
  },
  inputContainer: {
    flexDirection: "row",
    borderWidth: 1,
    borderRadius: 4,
    alignItems: "center",
  },
  flagButton: {
    paddingVertical: 12,
    paddingHorizontal: 6,
  },
  flagText: {
    fontSize: 24,
  },
  input: {
    flex: 1,
    paddingVertical: 12,
    fontSize: 16,
  },
  disabledInput: {
    backgroundColor: "#f0f0f0",
    color: "#a0a0a0",
  },
  errorSlot: {
    minHeight: 20,
    marginTop: 4,
  },
  errorText: {
    color: "red",
    fontSize: 12,
  },
  countryItem: {
    flexDirection: "row",
    alignItems: "center",
    padding: 10,
  },
  countryFlag: {
    fontSize: 24,
    marginRight: 10,
  },
  countryName: {
    fontSize: 18,
  },
});

VehiclePlateInput.displayName = "VehiclePlateInput";

export default VehiclePlateInput;
